/* wrapt-shop.js — behaviour for the stock journey sections (wrapt-shop.css).
   No framework and no build: plain DOM, initialised on load and again when the
   theme editor re-renders a section.

   Swipe rows (HlSwipeRow): on a phone, a track and two arrows under a row of
   peeking cards. The track's thumb is the visible share of the row, at its
   scroll position; an arrow disables at its end.

   Tile carousels (HlTileCarousel): each Gift Edits tile opens on the edit's
   photo, then its products. The tiles take turns: every few seconds the next
   tile in line glides one slide on (1.1s, eased), so only one moves at a time.
   Hovering or touching a tile holds it. No autoplay under reduced motion, and
   none while the band is off screen.

   Which products a tile shows is picked here, at random on every visit, from
   the list the section writes into it (Alan, 27 Sep 2026). Variety first: a
   maker and a kind of thing not yet in the tile -- the first picture's
   included -- then a new maker, then a new kind, then anything, so an edit
   short of makers or kinds still fills. A product another carousel is
   already showing is used only when the edit has nothing else left, and the
   edits with the fewest makers choose first. */
(function () {
  'use strict';

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /* ---- swipe rows ------------------------------------------------------ */

  function initSwipe(root) {
    var track = root.querySelector('[data-swipe-track]');
    var thumb = root.querySelector('[data-swipe-thumb]');
    var prev = root.querySelector('[data-swipe-prev]');
    var next = root.querySelector('[data-swipe-next]');
    if (!track || root.__wraptSwipe) return;
    root.__wraptSwipe = true;

    function measure() {
      var max = track.scrollWidth - track.clientWidth;
      if (thumb) {
        thumb.style.width = (track.scrollWidth ? (track.clientWidth / track.scrollWidth) * 100 : 100) + '%';
        thumb.style.left = (max > 0 ? (track.scrollLeft / track.scrollWidth) * 100 : 0) + '%';
      }
      if (prev) prev.disabled = track.scrollLeft < 8;
      if (next) next.disabled = track.scrollLeft > max - 8;
      /* Nothing to scroll: a row that shows its bar at every width (the type
         page's ready-made row) hides it on this. */
      root.toggleAttribute('data-swipe-fits', max <= 8);
    }

    function nudge(d) {
      var card = track.firstElementChild;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      track.scrollBy({ left: d * ((card ? card.offsetWidth : 240) + gap), behavior: reduced ? 'auto' : 'smooth' });
    }

    track.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });
    measure();
  }

  /* ---- tile carousels: which products, at random ------------------------ */

  var SIZES = '(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 240px';
  var HOLD_GIF = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  /* One maker however it is spelt: "WXY" and "wxy." are the same candles. */
  function makerKey(v) {
    return String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /* What the thing is: the last word of its name, once the maker, the
     variant, sizes and counts are off. "WXY Eos Candle - Orris Root" and
     "Mini Stack Candle - Ivory" are both "candle"; "Bubble Matches (Blue)"
     is "match"; "SKUNK | Bubblegum Soap" is "soap"; "Eucalyptus Wax Melt 60g
     by Helm London" is "melt". */
  function kindKey(title, maker) {
    var m = makerKey(maker);
    var parts = String(title || '').toLowerCase().split('|');
    var t = '';
    for (var i = 0; i < parts.length; i += 1) {
      var k = makerKey(parts[i]);
      if (k && !(m && m.indexOf(k) === 0)) { t = parts[i]; break; }
    }
    t = t.split(' - ')[0].split(':')[0]
      .replace(/\([^)]*\)/g, ' ')
      .replace(/\sby\s.*$/, ' ')
      .replace(/\b\d+\s*x\s*\d+\w*/g, ' ')
      .replace(/\bx\s*\d+\b/g, ' ')
      .replace(/\b\d+(\.\d+)?\s*(g|kg|ml|cl|l|oz|pack|pk|pcs)\b/g, ' ')
      .replace(/[^a-z\u00c0-\u024f\s]/g, ' ')
      .trim();
    var w = t.split(/\s+/).pop() || '';
    if (/(ch|sh|ss|x)es$/.test(w)) return w.slice(0, -2);
    if (/[^s]s$/.test(w)) return w.slice(0, -1);
    return w;
  }

  function shuffle(a) {
    for (var i = a.length - 1; i > 0; i -= 1) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  /* n from the pool, most varied first. `first` is the tile's opening
     picture when it is a product, so nothing repeats its maker or kind.
     `away` holds what the other carousels already show: those come after
     everything else, at every level of variety -- the same product twice on
     the band reads as a mistake, a maker twice in a tile does not. */
  function pickVaried(pool, n, first, away) {
    away = away || {};
    var list = shuffle(pool.map(function (p) {
      return { p: p, mk: makerKey(p.m), kd: kindKey(p.t, p.m) };
    }));
    var makers = {};
    var kinds = {};
    if (first && first.m) { makers[makerKey(first.m)] = true; kinds[kindKey(first.t, first.m)] = true; }
    var out = [];
    var passes = [
      function (x) { return !makers[x.mk] && !kinds[x.kd]; },
      function (x) { return !makers[x.mk]; },
      function (x) { return !kinds[x.kd]; },
      function () { return true; },
    ];
    [true, false].forEach(function (freshOnly) {
      passes.forEach(function (ok) {
        list.forEach(function (x) {
          if (out.length >= n || x.taken || (freshOnly && away[x.p.u]) || !ok(x)) return;
          x.taken = true;
          makers[x.mk] = true;
          kinds[x.kd] = true;
          out.push(x.p);
        });
      });
    });
    return out;
  }

  function poolOf(tile) {
    if (!tile.__wraptPool) {
      var data = tile.querySelector('[data-tile-pool]');
      try { tile.__wraptPool = (data && JSON.parse(data.textContent)) || []; } catch (_) { tile.__wraptPool = []; }
    }
    return tile.__wraptPool;
  }

  /* The slide the section used to write: the product whole on white, its
     name on a pill, out of the tab order (the edit's pill is the way in). */
  function productSlide(p) {
    var a = document.createElement('a');
    a.className = 'wrapt-etile__slide';
    a.href = p.u;
    a.tabIndex = -1;
    a.setAttribute('aria-hidden', 'true');
    var img = document.createElement('img');
    img.loading = 'lazy';
    img.alt = '';
    img.width = 900;
    img.height = Math.round(900 / (Number(p.r) || 1));
    img.sizes = SIZES;
    var set = [360, 540, 720, 900].map(function (w) {
      return p.i.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w';
    }).join(', ');
    /* Held until the page has loaded or it is on screen (snippets/wrapt-hold),
       so a carousel's photos never compete with the hero on a phone. */
    if (window.WraptHold) {
      img.src = HOLD_GIF;
      img.setAttribute('data-hold-srcset', set);
      img.setAttribute('data-hold-src', p.i);
    } else {
      img.srcset = set;
      img.src = p.i;
    }
    var pill = document.createElement('span');
    pill.className = 'wrapt-etile__pill';
    var name = document.createElement('span');
    name.textContent = p.t;
    pill.appendChild(name);
    a.appendChild(img);
    a.appendChild(pill);
    return a;
  }

  function fillTile(tile, shown) {
    var rail = tile.querySelector('[data-tile-rail]');
    if (!rail || !tile.querySelector('[data-tile-pool]') || tile.__wraptFilled) return;
    tile.__wraptFilled = true;
    shown = shown || {};
    var picks = pickVaried(poolOf(tile), parseInt(tile.getAttribute('data-max'), 10) || 5, {
      m: tile.getAttribute('data-maker'),
      t: tile.getAttribute('data-title'),
    }, shown);
    if (!picks.length) return;
    picks.forEach(function (p) {
      shown[p.u] = true;
      rail.appendChild(productSlide(p));
    });

    var total = picks.length + 1;
    var label = tile.getAttribute('data-name') || '';
    var dots = document.createElement('div');
    dots.className = 'wrapt-etile__dots';
    for (var k = 1; k <= total; k += 1) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'wrapt-etile__dot';
      b.setAttribute('data-tile-dot', '');
      b.setAttribute('aria-label', label + ': picture ' + k + ' of ' + total);
      b.setAttribute('aria-current', k === 1 ? 'true' : 'false');
      dots.appendChild(b);
    }
    tile.appendChild(dots);
    if (window.WraptHold) window.WraptHold.watch(tile);
  }

  /* ---- tile carousels -------------------------------------------------- */

  function initTile(tile) {
    fillTile(tile);
    var rail = tile.querySelector('[data-tile-rail]');
    var dots = Array.prototype.slice.call(tile.querySelectorAll('[data-tile-dot]'));
    if (!rail || tile.__wraptTile) return null;

    var state = { at: 0, hold: false };

    function mark(k) {
      state.at = k;
      dots.forEach(function (d, i) { d.setAttribute('aria-current', i === k ? 'true' : 'false'); });
    }

    function glide(k) {
      /* The photo it is moving to, if still held, starts now, not mid-glide. */
      var next = rail.children[k] && rail.children[k].querySelector('img[data-hold-src]');
      if (next && window.WraptHold) window.WraptHold.release(next);
      var from = rail.scrollLeft;
      var to = k * rail.clientWidth;
      if (reduced) { rail.scrollLeft = to; return; }
      var t0 = performance.now();
      var dur = 1100;
      rail.style.scrollSnapType = 'none';
      function step(now) {
        var t = Math.min(1, (now - t0) / dur);
        rail.scrollLeft = from + (to - from) * ease(t);
        if (t < 1) requestAnimationFrame(step);
        else rail.style.scrollSnapType = '';
      }
      requestAnimationFrame(step);
    }

    rail.addEventListener('scroll', function () {
      var k = Math.round(rail.scrollLeft / Math.max(1, rail.clientWidth));
      if (k !== state.at) mark(k);
    }, { passive: true });

    dots.forEach(function (d, i) { d.addEventListener('click', function () { glide(i); }); });
    tile.addEventListener('mouseenter', function () { state.hold = true; });
    tile.addEventListener('mouseleave', function () { state.hold = false; });
    tile.addEventListener('touchstart', function () { state.hold = true; }, { passive: true });
    tile.addEventListener('focusin', function () { state.hold = true; });
    tile.addEventListener('focusout', function () { state.hold = false; });

    tile.__wraptTile = true;
    return {
      advance: function () {
        var n = dots.length;
        if (state.hold || n < 2) return;
        glide((state.at + 1) % n);
      },
    };
  }

  function initEdits(section) {
    if (section.__wraptEdits) return;
    section.__wraptEdits = true;

    /* Every tile's opening product counts as shown; then the tiles with the
       fewest makers to choose from pick first, so the big edits -- What's
       New, Best Sellers -- are the ones that step round a repeat. (Makers,
       not products: every list is capped at the same length, and Treats'
       24 hold far fewer makers than What's New's.) */
    var all = Array.prototype.slice.call(section.querySelectorAll('[data-tile]'));
    var shown = {};
    all.forEach(function (t) {
      var c = t.getAttribute('data-cover');
      if (c) shown[c] = true;
    });
    var makersIn = function (t) {
      var seen = {};
      poolOf(t).forEach(function (p) { seen[makerKey(p.m)] = true; });
      return Object.keys(seen).length;
    };
    all.slice()
      .sort(function (a, b) { return makersIn(a) - makersIn(b) || poolOf(a).length - poolOf(b).length; })
      .forEach(function (t) { fillTile(t, shown); });

    var tiles = all.map(function (t) { return initTile(t); }).filter(Boolean);
    var auto = section.getAttribute('data-autoplay') === 'true';
    var speed = parseFloat(section.getAttribute('data-speed')) || 2.4;
    if (!auto || reduced || !tiles.length) return;

    var visible = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { visible = es[0].isIntersecting; }).observe(section);
    }

    /* The tiles take turns, left to right: one glide per tick. */
    var tick = 0;
    setInterval(function () {
      if (!visible || document.hidden) return;
      tiles[tick % tiles.length].advance();
      tick += 1;
    }, speed * 1000);
  }

  /* ---- an edit's page: Vegan and sort, in the page ---------------------- */

  function initEditPage(root) {
    if (root.__wraptEdit) return;
    root.__wraptEdit = true;
    var grid = root.querySelector('[data-edit-grid]');
    var veg = root.querySelector('[data-edit-vegan]');
    var sort = root.querySelector('[data-edit-sort]');
    var count = root.querySelector('[data-edit-count]');
    var none = root.querySelector('[data-edit-none]');
    /* On a phone the chips are one row that swipes: bring the current one
       into view (scrollLeft, as the design asks -- not scrollIntoView, which
       would scroll the page too). */
    var chips = root.querySelector('[data-edit-chips]');
    var here = chips && chips.querySelector('[aria-current]');
    if (here && chips.scrollWidth > chips.clientWidth) chips.scrollLeft = here.offsetLeft - 20;

    if (!grid) return;
    var items = Array.prototype.slice.call(grid.querySelectorAll('[data-edit-item]'));
    /* The type template (D138): type chips, one on at a time, matching the
       product tags each card carries in data-tags; and the Build Your Own
       promo, kept after the Nth card still showing (or the last). */
    var types = Array.prototype.slice.call(root.querySelectorAll('[data-edit-type]'));
    var promo = grid.querySelector('[data-edit-promo]');
    var promoAfter = parseInt(grid.getAttribute('data-promo-after'), 10) || 0;

    function apply() {
      var onlyVeg = veg && veg.getAttribute('aria-pressed') === 'true';
      var typeOn = types.filter(function (b) { return b.getAttribute('aria-pressed') === 'true'; })[0];
      var want = typeOn ? ' ' + typeOn.getAttribute('data-edit-type') + ' ' : null;
      var by = sort ? sort.value : 'featured';
      var list = items.slice().sort(function (a, b) {
        if (by === 'low') return a.dataset.price - b.dataset.price;
        if (by === 'high') return b.dataset.price - a.dataset.price;
        return a.dataset.order - b.dataset.order;
      });
      var shown = 0;
      list.forEach(function (el) {
        var on = (!onlyVeg || el.hasAttribute('data-vegan')) &&
          (!want || (' ' + (el.getAttribute('data-tags') || '') + ' ').indexOf(want) > -1);
        el.hidden = !on;
        if (on) shown += 1;
        grid.appendChild(el);
        if (promo && on && shown === promoAfter) grid.appendChild(promo);
      });
      if (promo) {
        if (shown < promoAfter) grid.appendChild(promo);
        promo.hidden = shown === 0;
      }
      if (count) count.textContent = shown + (shown === 1 ? ' thing' : ' things');
      if (none) none.hidden = shown > 0;
    }

    if (veg) veg.addEventListener('click', function () {
      veg.setAttribute('aria-pressed', veg.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      apply();
    });
    types.forEach(function (b) {
      b.addEventListener('click', function () {
        var was = b.getAttribute('aria-pressed') === 'true';
        types.forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
        if (!was) b.setAttribute('aria-pressed', 'true');
        apply();
      });
    });
    if (sort) sort.addEventListener('change', apply);
  }

  /* ---- the stock item page: gallery and the bundle ---------------------- */

  var money = (function () {
    try {
      var f = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
      return function (p) { return f.format(p / 100); };
    } catch (_) {
      return function (p) { return '£' + (p / 100).toFixed(2); };
    }
  })();

  function initStock(root) {
    if (root.__wraptStock) return;
    root.__wraptStock = true;

    var rail = root.querySelector('[data-stock-rail]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-stock-thumb]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-stock-dot]'));
    var count = root.querySelector('[data-stock-count]');
    if (rail) {
      var n = rail.children.length;
      var mark = function (k) {
        thumbs.forEach(function (t, i) { t.setAttribute('aria-current', i === k ? 'true' : 'false'); });
        dots.forEach(function (d, i) { d.classList.toggle('is-on', i === k); });
        if (count) count.textContent = (k + 1) + ' / ' + n;
      };
      rail.addEventListener('scroll', function () {
        mark(Math.round(rail.scrollLeft / Math.max(1, rail.clientWidth)));
      }, { passive: true });
      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () {
          rail.scrollTo({ left: i * rail.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
          mark(i);
        });
      });
    }

    var bundle = root.querySelector('[data-stock-bundle]');
    if (!bundle) return;
    var base = parseInt(bundle.getAttribute('data-base'), 10) || 0;
    var min = parseInt(bundle.getAttribute('data-min'), 10) || 1;
    var byo = bundle.getAttribute('data-byo');
    var label = bundle.getAttribute('data-label');
    var rows = Array.prototype.slice.call(bundle.querySelectorAll('[data-bundle-row]'));
    var go = bundle.querySelector('[data-bundle-go]');
    var cnt = bundle.querySelector('[data-bundle-count]');
    var tot = bundle.querySelector('[data-bundle-total]');
    var note = bundle.querySelector('[data-bundle-note]');

    function update() {
      var on = rows.filter(function (r) { return r.hasAttribute('data-on'); });
      var n = on.length;
      var sum = on.reduce(function (s, r) { return s + (parseInt(r.getAttribute('data-price'), 10) || 0); }, base);
      if (cnt) cnt.textContent = n + (n === 1 ? ' item' : ' items') + ' in a box';
      if (tot) tot.textContent = money(sum);
      if (go) {
        go.textContent = n > 1 ? 'Start a box with these ' + n : label;
        go.setAttribute('href', byo + '?add=' + on.map(function (r) { return r.getAttribute('data-handle'); }).join(','));
      }
      var left = Math.max(0, min - n);
      if (note) note.textContent = left ? 'Add ' + left + ' more in the builder to reach the minimum of ' + min + '.' : 'That’s the minimum covered. Personalise it next.';
    }

    Array.prototype.forEach.call(bundle.querySelectorAll('[data-bundle-toggle]'), function (b) {
      b.addEventListener('click', function () {
        var row = b.closest('[data-bundle-row]');
        var was = b.getAttribute('aria-pressed') === 'true';
        b.setAttribute('aria-pressed', was ? 'false' : 'true');
        if (was) row.removeAttribute('data-on'); else row.setAttribute('data-on', '');
        update();
      });
    });
  }

  /* ---- boot ------------------------------------------------------------ */

  function boot(scope) {
    var root = scope || document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-swipe]'), initSwipe);
    Array.prototype.forEach.call(root.querySelectorAll('[data-wrapt-edits]'), initEdits);
    Array.prototype.forEach.call(root.querySelectorAll('[data-wrapt-edit-page]'), initEditPage);
    Array.prototype.forEach.call(root.querySelectorAll('[data-wrapt-stock]'), initStock);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  window.WraptShop = { boot: boot, kindKey: kindKey, makerKey: makerKey, pickVaried: pickVaried };
})();
